var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import React, { Suspense, useState } from 'react';
import { Navigate, unstable_HistoryRouter as Router, useRoutes } from 'react-router-dom';
import { TV_PAGE } from '@tving/utils/src/constants/commonConstants';
import 'dayjs/locale/ko';
import lazyRetry from '@tving/utils/src/utils/common/lazyRetry/lazyRetry';
import { FocusRootContext } from '@tving/utils/src/utils/tving-focusable/hooks/useFocusedContext';
import TvingSpinner from '@tving/utils/src/utils/tving-spinner/TvingSpinner';
import { PageNavigator } from '@/index';
import EntryLayer from '@layers/entry/EntryLayer';
import SplashLayer from '@layers/splash/SplashLayer';
import RedirectPage from '@pages/RedirectPage';
import PopupLayer from './layers/popup/PopupLayer';
import ToastLayer from './layers/toast/ToastLayer';
var MainPage = lazyRetry(function () { return import('@pages/main/MainPage'); });
var OnboardingPage = lazyRetry(function () { return import('@pages/account/onBoarding/OnboardingPage'); });
var QrPage = lazyRetry(function () { return import('@pages/account/qr/QrPage'); });
var WelcomePage = lazyRetry(function () { return import('@pages/account/welcome/WelcomePage'); });
var LogoutPage = lazyRetry(function () { return import('@pages/account/logout/LogoutPage'); });
var BillingAffiliatePage = lazyRetry(function () { return import('@pages/billing/BillingAffiliatePage'); });
var BillingCautionPage = lazyRetry(function () { return import('@pages/billing/BillingCautionPage'); });
var BillingPage = lazyRetry(function () { return import('@pages/billing/BillingPage'); });
var BillingHistoryPage = lazyRetry(function () { return import('@pages/billing/history/BillingHistoryPage'); });
var EmergencyPage = lazyRetry(function () { return import('@pages/EmergencyPage'); });
var ErrorPage = lazyRetry(function () { return import('@pages/error/ErrorPage'); });
var PlayerBlockPage = lazyRetry(function () { return import('@pages/player/PlayerBlockPage'); });
var ProfileListPage = lazyRetry(function () { return import('@pages/profile/list/ProfileListPage'); });
var ProfileEditPage = lazyRetry(function () { return import('@pages/profile/edit/ProfileEditPage'); });
var ProfileImageListPage = lazyRetry(function () { return import('@pages/profile/profileImageList/ProfileImageListPage'); });
var PasswordPage = lazyRetry(function () { return import('@pages/profile/password/PasswordPage'); });
var PasswordResetPage = lazyRetry(function () { return import('@pages/profile/reset/PasswordResetPage'); });
var SettingPage = lazyRetry(function () { return import('@pages/setting/SettingPage'); });
var SmsPage = lazyRetry(function () { return import('@pages/sms/SmsPage'); });
var CertificationPage = lazyRetry(function () { return import('@pages/certification/CertificationPage'); });
var TvLoginPage = lazyRetry(function () { return import('@pages/account/tv/TvLoginPage'); });
var TvLoginInputPage = lazyRetry(function () { return import('@pages/account/tv/TvLoginInputPage'); });
var LoginPage = lazyRetry(function () { return import('@pages/account/login/LoginPage'); });
var App = function () {
    var DefaultRoutes = function () {
        return useRoutes([
            { path: '/', element: _jsx(RedirectPage, {}) },
            { path: '/home', element: _jsx(Navigate, { to: "/main" }) },
            // 온보딩 및 로그인
            { path: '/onboarding', element: _jsx(OnboardingPage, {}) },
            { path: '/join', element: _jsx(QrPage, {}) },
            { path: '/login', element: _jsx(LoginPage, {}) },
            { path: '/login/qr', element: _jsx(QrPage, {}) },
            { path: '/login/tv', element: _jsx(TvLoginPage, {}) },
            { path: '/login/tv/input', element: _jsx(TvLoginInputPage, {}) },
            { path: '/welcome/join', element: _jsx(WelcomePage, {}) },
            { path: '/welcome/login', element: _jsx(WelcomePage, {}) },
            { path: '/logout', element: _jsx(LogoutPage, {}) },
            // 프로필
            { path: '/profile', element: _jsx(ProfileListPage, {}) },
            { path: '/profile/list', element: _jsx(ProfileListPage, {}) },
            { path: '/profile/add', element: _jsx(ProfileEditPage, {}) },
            { path: '/profile/modify', element: _jsx(ProfileEditPage, {}) },
            { path: '/profile/password', element: _jsx(PasswordPage, {}) },
            { path: '/profile/password/reset', element: _jsx(PasswordResetPage, {}) },
            { path: '/profile/profileImageList', element: _jsx(ProfileImageListPage, {}) },
            // 이용권
            { path: '/purchase', element: _jsx(BillingPage, {}) },
            { path: '/purchase/:mediaCode', element: _jsx(BillingPage, {}) },
            { path: '/purchase/mobile', element: _jsx(BillingPage, {}) },
            { path: '/purchase/inapp', element: _jsx(BillingPage, {}) },
            { path: '/purchase/inapp/caution', element: _jsx(BillingCautionPage, {}) },
            { path: '/purchase/mobile/:mediaCode', element: _jsx(BillingPage, {}) },
            { path: '/purchase/inapp/:mediaCode', element: _jsx(BillingPage, {}) },
            { path: '/purchase/affiliate/:affiliateId', element: _jsx(BillingAffiliatePage, {}) },
            // SMS
            { path: '/sms/:smsType', element: _jsx(SmsPage, {}) },
            { path: '/sms/:smsType/:itemId', element: _jsx(SmsPage, {}) },
            // Certification
            { path: '/certification/:smsType', element: _jsx(CertificationPage, {}) },
            { path: '/certification/:smsType/:itemId', element: _jsx(CertificationPage, {}) },
            //
            { path: '/player-block', element: _jsx(PlayerBlockPage, {}) },
            { path: '/error/:errorCode', element: _jsx(ErrorPage, {}) },
            { path: '/ttv', element: _jsx(EmergencyPage, {}) },
            { path: '/setting', element: _jsx(SettingPage, {}) },
            { path: '/setting/:settingMenuId', element: _jsx(SettingPage, {}) },
            { path: '/purchase/history', element: _jsx(BillingHistoryPage, {}) },
            { path: '*', element: null },
        ]);
    };
    var MainRoutes = function () {
        return useRoutes([
            { path: '/', element: null },
            { path: '/login', element: null },
            { path: '/login/qr', element: null },
            { path: '/login/tv', element: null },
            { path: '/login/tv/input', element: null },
            { path: '/join', element: null },
            { path: '/profile', element: null },
            { path: '/profile/*', element: null },
            { path: '/onboarding', element: null },
            { path: '/welcome/join', element: null },
            { path: '/welcome/login', element: null },
            { path: '*', element: _jsx(MainPage, {}) },
        ]);
    };
    var _a = __read(useState(false), 2), isSplashPlayComplete = _a[0], setIsSplashPlayComplete = _a[1];
    var _b = __read(useState(false), 2), isLoaded = _b[0], setIsLoaded = _b[1];
    return (_jsxs(Router, __assign({ history: PageNavigator.getHistory() }, { children: [!isLoaded ? _jsx(SplashLayer, { setIsSplashPlayComplete: setIsSplashPlayComplete }) : null, isSplashPlayComplete ? (_jsxs(_Fragment, { children: [_jsx(EntryLayer, { setIsLoaded: setIsLoaded }), _jsxs(Suspense, __assign({ fallback: _jsx(TvingSpinner, { delay: 0 }) }, { children: [_jsx(MainRoutes, {}), _jsx(DefaultRoutes, {})] })), _jsx(FocusRootContext.Provider, __assign({ value: TV_PAGE.TOAST }, { children: _jsx(ToastLayer, {}) })), _jsx(FocusRootContext.Provider, __assign({ value: TV_PAGE.POPUP }, { children: _jsx(PopupLayer, {}) }))] })) : null] })));
};
export default App;
